import React from "react";

export const Monogram = ({ size = 40, className = "", stroke = "#FAF9F6", withStars = false }) => (
  <svg
    width={size * (60 / 113)}
    height={size}
    viewBox="0 0 60 113"
    fill="none"
    className={className}
    aria-label="OBSIDIAN monogram"
    data-testid="obsidian-monogram"
  >
    <line x1="30" y1="8.3" x2="30" y2="75.3" stroke={stroke} strokeWidth="0.8" opacity="0.95" />
    <circle cx="30" cy="41.8" r="29.5" stroke={stroke} strokeWidth="0.55" opacity="0.95" />
    <circle cx="30" cy="41.8" r="13.6" stroke={stroke} strokeWidth="1.7" opacity="1" />
    <circle cx="30" cy="41.8" r="6.8" stroke={stroke} strokeWidth="0.75" opacity="1" />
    <circle cx="30" cy="20.3" r="2.2" fill={stroke} />
    <circle cx="30" cy="63.5" r="2.2" fill={stroke} />
    {withStars && (
      <path
        d="M46 -6c.35 2.6 1.75 4 4.6 4.4-2.85.4-4.25 1.8-4.6 4.4-.35-2.6-1.75-4-4.6-4.4 2.85-.4 4.25-1.8 4.6-4.4Z"
        fill={stroke}
        opacity="0.9"
      />
    )}
  </svg>
);

export const Star = ({ size = 12, className = "", fill = "#C5A059", style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} className={className} style={style} aria-hidden="true">
    <path d="M12 0c.9 6.6 4.5 10.2 12 12-7.5 1.8-11.1 5.4-12 12-.9-6.6-4.5-10.2-12-12C7.5 10.2 11.1 6.6 12 0Z" />
  </svg>
);

const STAR_POS = [
  { top: "18%", left: "12%", s: 10, d: "0s", dur: "5s" },
  { top: "30%", left: "84%", s: 7, d: "1.2s", dur: "4.2s" },
  { top: "62%", left: "6%", s: 6, d: "2.1s", dur: "6s" },
  { top: "74%", left: "90%", s: 9, d: "0.6s", dur: "5.4s" },
  { top: "12%", left: "62%", s: 6, d: "1.8s", dur: "4.8s" },
  { top: "82%", left: "30%", s: 7, d: "2.8s", dur: "5.8s" },
  { top: "48%", left: "76%", s: 5, d: "3.4s", dur: "4.4s" },
];

export const Starfield = ({ className = "" }) => (
  <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden="true">
    {STAR_POS.map((p, i) => (
      <div
        key={i}
        className="star-twinkle absolute"
        style={{ top: p.top, left: p.left, "--tw-delay": p.d, "--tw-duration": p.dur }}
      >
        <Star size={p.s} fill="#FAF9F6" />
      </div>
    ))}
  </div>
);

export const Wordmark = ({ size = "sm", tagline = true }) => {
  const big = size === "lg";
  if (big) {
    return (
      <div className="flex flex-col items-center text-center select-none">
        <img
          src="/media/logo_white.png"
          alt="OBSIDIAN Private Driver"
          className="h-auto w-[min(86vw,600px)]"
          data-testid="hero-logo"
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center text-center select-none">
      <div className="mb-2">
        <Monogram size={30} />
      </div>
      <div
        className="font-light text-[#FAF9F6]"
        style={{
          fontSize: "0.95rem",
          letterSpacing: "0.34em",
          marginLeft: "0.34em",
          fontWeight: 200,
          fontFamily: "var(--font-title-solid)",
        }}
      >
        OBSIDIAN
      </div>
      {tagline && (
        <div
          className="uppercase text-[#FAF9F6]/70"
          style={{
            fontSize: "0.42rem",
            letterSpacing: "0.5em",
            marginLeft: "0.5em",
            marginTop: "0.35rem",
            fontWeight: 300,
          }}
        >
          Private Driver
        </div>
      )}
    </div>
  );
};


const FOOTER_STARS = [
  { left: "4%", bottom: 90, s: 6, d: "0.4s", dur: "5.6s", o: 0.22 },
  { left: "12%", bottom: 30, s: 9, d: "1.6s", dur: "6.2s", o: 0.3 },
  { left: "21%", bottom: 120, s: 5, d: "2.4s", dur: "5s", o: 0.18 },
  { left: "30%", bottom: 55, s: 7, d: "0.9s", dur: "5.8s", o: 0.26 },
  { left: "39%", bottom: 20, s: 5, d: "3.1s", dur: "4.6s", o: 0.2 },
  { left: "48%", bottom: 95, s: 10, d: "1.2s", dur: "6.4s", o: 0.32 },
  { left: "57%", bottom: 40, s: 6, d: "2.8s", dur: "5.2s", o: 0.22 },
  { left: "65%", bottom: 130, s: 5, d: "0.6s", dur: "4.8s", o: 0.16 },
  { left: "73%", bottom: 70, s: 8, d: "2s", dur: "6s", o: 0.28 },
  { left: "81%", bottom: 25, s: 5, d: "3.6s", dur: "5.4s", o: 0.2 },
  { left: "89%", bottom: 105, s: 7, d: "1.4s", dur: "5.6s", o: 0.24 },
  { left: "96%", bottom: 45, s: 5, d: "2.6s", dur: "4.9s", o: 0.18 },
];

export const FooterStars = () => (
  <div
    className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-52 overflow-hidden"
    aria-hidden="true"
    data-testid="footer-stars"
  >
    {FOOTER_STARS.map((p, i) => (
      <span key={i} className="absolute" style={{ left: p.left, bottom: p.bottom, opacity: p.o }}>
        <span className="star-twinkle block" style={{ "--tw-delay": p.d, "--tw-duration": p.dur }}>
          <Star size={p.s} fill="#FAF9F6" />
        </span>
      </span>
    ))}
  </div>
);